// src/pages/ClientProfitabilityPage.js
//
// Client profitability profile.
//   • List view (no :clientId): pick a client.
//   • Profile view (:clientId): enter the work we did for the client (deliverables + who executed
//     each), track monthly quotas, and — for Manager / Sales Manager only — see revenue, cost and
//     profit for the month.
//
// Visibility: Account Manager enters deliverables and sees quotas, but NEVER sees money.
// Manager + Sales Manager see + edit the financials (retainer, extra charges, quotas) and see cost.
//
// Cost model:
//   employee cost for a deliverable = (monthlySalary / workingDaysPerMonth) / (that employee's
//     number of deliverables that same day, across ALL clients)
//   freelancer cost = the freelancer's rate for that deliverable type
//   + any manual extra costs on the deliverable
// Revenue (month) = client.monthlyRetainer + sum of that month's extra charges.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection, collectionGroup, query, where, getDocs, orderBy, limit, doc, getDoc,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { DELIVERABLE_TYPES, deliverableLabel, deliverableIcon } from '../config/deliverables';
import styles from '../styles/ClientProfitabilityPage.module.css';

const MONEY_ROLES = ['Manager', 'Sales Manager'];
const ENTER_ROLES = ['Manager', 'Sales Manager', 'Accounts Manager'];
const isTerminated = (u) =>
  (u.employmentStatus || (u.active === false ? 'Terminated' : 'Active')) === 'Terminated';
const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const monthOf = (dateStr) => (dateStr || '').slice(0, 7);

// ---- Date-range presets (shared shape with Work by Person) ----
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const PRESETS = [
  { key: 'all', ar: 'الكل', en: 'All' },
  { key: 'thisMonth', ar: 'هذا الشهر', en: 'This month' },
  { key: 'lastMonth', ar: 'الشهر الماضي', en: 'Last month' },
  { key: 'last28', ar: 'آخر 28 يوم', en: 'Last 28 days' },
  { key: 'thisWeek', ar: 'هذا الأسبوع', en: 'This week' },
  { key: 'lastWeek', ar: 'الأسبوع الماضي', en: 'Last week' },
  { key: 'last7', ar: 'آخر 7 أيام', en: 'Last 7 days' },
  { key: 'today', ar: 'اليوم', en: 'Today' },
  { key: 'yesterday', ar: 'أمس', en: 'Yesterday' },
  { key: 'thisYear', ar: 'هذه السنة', en: 'This year' },
  { key: 'lastYear', ar: 'السنة الماضية', en: 'Last year' },
  { key: 'custom', ar: 'مخصص', en: 'Custom' },
];

const rangeFor = (preset, customStart, customEnd) => {
  const today = new Date();
  const daysSinceSat = (today.getDay() + 1) % 7;
  const weekStart = addDays(today, -daysSinceSat);
  const y = today.getFullYear(), m = today.getMonth();
  switch (preset) {
    case 'all': return { start: null, end: null };
    case 'today': return { start: ymd(today), end: ymd(today) };
    case 'yesterday': { const d = addDays(today, -1); return { start: ymd(d), end: ymd(d) }; }
    case 'last7': return { start: ymd(addDays(today, -6)), end: ymd(today) };
    case 'last28': return { start: ymd(addDays(today, -27)), end: ymd(today) };
    case 'thisWeek': return { start: ymd(weekStart), end: ymd(addDays(weekStart, 6)) };
    case 'lastWeek': return { start: ymd(addDays(weekStart, -7)), end: ymd(addDays(weekStart, -1)) };
    case 'thisMonth': return { start: ymd(new Date(y, m, 1)), end: ymd(new Date(y, m + 1, 0)) };
    case 'lastMonth': return { start: ymd(new Date(y, m - 1, 1)), end: ymd(new Date(y, m, 0)) };
    case 'thisYear': return { start: `${y}-01-01`, end: `${y}-12-31` };
    case 'lastYear': return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
    case 'custom': return { start: customStart || null, end: customEnd || null };
    default: return { start: null, end: null };
  }
};
const inRange = (dateStr, start, end) => {
  if (!dateStr) return false;
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
};
// List of YYYY-MM months from start..end inclusive (both YYYY-MM-DD). Empty if unbounded.
const monthsBetween = (start, end) => {
  if (!start || !end) return null;
  const out = [];
  let y = +start.slice(0, 4), mo = +start.slice(5, 7);
  const ey = +end.slice(0, 4), em = +end.slice(5, 7);
  while (y < ey || (y === ey && mo <= em)) {
    out.push(`${y}-${pad2(mo)}`);
    mo += 1; if (mo > 12) { mo = 1; y += 1; }
    if (out.length > 240) break; // safety
  }
  return out;
};

const T = {
  ar: {
    listTitle: 'ربحية العملاء', listSub: 'اختر زبون لعرض شغله وربحيته',
    search: 'بحث عن زبون…', noClients: 'لا يوجد عملاء.',
    ongoingSection: '⭐ عملاء مستمرون', othersSection: 'باقي العملاء',
    ongoingOnly: 'المستمرون فقط', allClients: 'الكل',
    owes: 'عليه مصاري مش محصّلة',
    markOngoing: 'تعليم كعميل مستمر', unmarkOngoing: 'إلغاء التعليم', ongoingBadge: 'مستمر',
    noOngoing: 'لا يوجد عملاء مستمرون بعد — اضغط النجمة على أي زبون لتعليمه.',
    back: '→ رجوع للقائمة', month: 'الشهر',
    modeMonth: 'شهر', modePeriod: 'فترة', from: 'من', to: 'إلى',
    deliverables: 'الشغلات المنفّذة', addDeliverable: 'إضافة شغلة',
    type: 'النوع', quantity: 'العدد', title: 'العنوان (اختياري)', date: 'التاريخ', executors: 'المنفّذون',
    employee: 'موظف', freelancer: 'فريلانسر', addEmp: 'أضف موظف', addFree: 'أضف فريلانسر',
    save: 'حفظ', cancel: 'إلغاء', del: 'حذف', noDeliverables: 'لا يوجد شغلات هذا الشهر.',
    quotas: 'الحصص الشهرية', quotaOf: 'من', done: 'منجز',
    financials: 'المالية (الإيراد / التكلفة / الربح)', revenue: 'الإيراد المستحق', cost: 'التكلفة', profit: 'الربح',
    collected: 'المحصّل', outstanding: 'غير محصّل',
    collections: 'التحصيلات (المبالغ المستلمة)', addCollection: 'إضافة تحصيل',
    retainer: 'الرسوم الشهرية', charges: 'إيرادات إضافية', addCharge: 'إضافة إيراد',
    amount: 'المبلغ', description: 'الوصف', currency: '₪',
    editFin: 'تعديل المالية', quotaTargets: 'أهداف الحصص الشهرية', saveFin: 'حفظ المالية', monthField: 'الشهر',
    extraCost: 'تكلفة إضافية', addExtraCost: '+ تكلفة', pickType: 'اختر النوع',
    typeRequired: 'اختر نوع الشغلة.', execRequired: 'أضف منفّذ واحد على الأقل.',
    confirmDelDeliverable: 'حذف هذه الشغلة؟', costHidden: 'التكاليف مخفية عن دورك.',
    perDay: 'يومي',
    editCosts: 'تعديل أسعار الفريلانسرز', unitPrice: 'السعر (للوحدة)', standardRate: 'المعتمد',
    revertHint: 'اتركه فاضي للرجوع للسعر المعتمد', noFreelancersOnDeliverable: 'لا يوجد فريلانسر على هذه الشغلة.',
    saveEdit: 'حفظ الأسعار', editDeliverable: 'تعديل الشغلة', edit: 'تعديل', price: 'السعر',
  },
  en: {
    listTitle: 'Client Profitability', listSub: 'Pick a client to see their work and profit',
    search: 'Search client…', noClients: 'No clients.',
    ongoingSection: '⭐ Ongoing clients', othersSection: 'Other clients',
    ongoingOnly: 'Ongoing only', allClients: 'All',
    owes: 'Outstanding',
    markOngoing: 'Mark as ongoing client', unmarkOngoing: 'Unmark', ongoingBadge: 'Ongoing',
    noOngoing: 'No ongoing clients yet — tap the star on any client to mark it.',
    back: '← Back to list', month: 'Month',
    modeMonth: 'Month', modePeriod: 'Period', from: 'From', to: 'To',
    deliverables: 'Deliverables', addDeliverable: 'Add deliverable',
    type: 'Type', quantity: 'Qty', title: 'Title (optional)', date: 'Date', executors: 'Executors',
    employee: 'Employee', freelancer: 'Freelancer', addEmp: 'Add employee', addFree: 'Add freelancer',
    save: 'Save', cancel: 'Cancel', del: 'Delete', noDeliverables: 'No deliverables this month.',
    quotas: 'Monthly quotas', quotaOf: 'of', done: 'done',
    financials: 'Financials (revenue / cost / profit)', revenue: 'Billed revenue', cost: 'Cost', profit: 'Profit',
    collected: 'Collected', outstanding: 'Outstanding',
    collections: 'Collections (amounts received)', addCollection: 'Add collection',
    retainer: 'Monthly retainer', charges: 'Extra revenue', addCharge: 'Add revenue',
    amount: 'Amount', description: 'Description', currency: '₪',
    editFin: 'Edit financials', quotaTargets: 'Monthly quota targets', saveFin: 'Save financials', monthField: 'Month',
    extraCost: 'Extra cost', addExtraCost: '+ cost', pickType: 'Pick type',
    typeRequired: 'Pick a deliverable type.', execRequired: 'Add at least one executor.',
    confirmDelDeliverable: 'Delete this deliverable?', costHidden: 'Costs are hidden for your role.',
    perDay: '/day',
    editCosts: 'Edit freelancer prices', unitPrice: 'Price (per unit)', standardRate: 'standard',
    revertHint: 'Leave empty to use the standard rate', noFreelancersOnDeliverable: 'No freelancer on this deliverable.',
    saveEdit: 'Save prices', editDeliverable: 'Edit deliverable', edit: 'Edit', price: 'Price',
  },
};

export default function ClientProfitabilityPage() {
  const { clientId } = useParams();
  const { userRole, currentUser } = useAuth();
  const { language, isRTL, toggleLanguage } = useSocialLanguage();
  const t = T[language] || T.ar;

  const canSeeMoney = MONEY_ROLES.includes(userRole);
  const canEnter = ENTER_ROLES.includes(userRole);

  if (!clientId) return <ClientPicker t={t} isRTL={isRTL} language={language} toggleLanguage={toggleLanguage} canSeeMoney={canSeeMoney} />;
  return (
    <ClientProfile
      key={clientId}
      clientId={clientId}
      t={t}
      isRTL={isRTL}
      language={language}
      toggleLanguage={toggleLanguage}
      userRole={userRole}
      currentUser={currentUser}
      canSeeMoney={canSeeMoney}
      canEnter={canEnter}
    />
  );
}

// ---------------- List view ----------------
function ClientPicker({ t, isRTL, language, toggleLanguage, canSeeMoney }) {
  const [clients, setClients] = useState([]);
  const [lastWorked, setLastWorked] = useState({}); // clientId -> most recent deliverable date (YYYY-MM-DD)
  const [outstanding, setOutstanding] = useState({}); // clientId -> billed - collected (money roles only)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const base = [
          getDocs(query(collection(db, 'clients'), orderBy('name', 'asc'))),
          // Recent deliverables (any client) → latest work date per client, to sort by recency.
          getDocs(query(collection(db, 'deliverables'), orderBy('date', 'desc'), limit(600))),
        ];
        // Charges + collections across all clients (money roles only) → outstanding per client.
        if (canSeeMoney) {
          base.push(getDocs(collectionGroup(db, 'charges')));
          base.push(getDocs(collectionGroup(db, 'collections')));
        }
        const [cSnap, dSnap, chSnap, coSnap] = await Promise.all(base);

        const clientList = cSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClients(clientList);

        const lw = {};
        dSnap.docs.forEach((d) => {
          const x = d.data();
          if (x.clientId && x.date && !lw[x.clientId]) lw[x.clientId] = x.date; // first seen = most recent
        });
        setLastWorked(lw);

        if (canSeeMoney) {
          const billed = {};   // clientId -> retainers(sum of per-month) + charges
          const collected = {};
          clientList.forEach((c) => {
            billed[c.id] = Object.values(c.monthlyRetainers || {}).reduce((s, v) => s + num(v), 0);
          });
          chSnap?.docs.forEach((d) => {
            const cid = d.ref.parent.parent?.id;
            if (cid) billed[cid] = (billed[cid] || 0) + num(d.data().amount);
          });
          coSnap?.docs.forEach((d) => {
            const cid = d.ref.parent.parent?.id;
            if (cid) collected[cid] = (collected[cid] || 0) + num(d.data().amount);
          });
          const out = {};
          clientList.forEach((c) => { out[c.id] = (billed[c.id] || 0) - (collected[c.id] || 0); });
          setOutstanding(out);
        }
      } catch (e) { console.error('load clients failed', e); }
      finally { setLoading(false); }
    })();
  }, [canSeeMoney]);

  const [onlyOngoing, setOnlyOngoing] = useState(false);

  const toggleOngoing = async (e, c) => {
    e.preventDefault(); e.stopPropagation();
    const next = !(c.isOngoing === true);
    setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, isOngoing: next } : x)));
    try {
      await updateDoc(doc(db, 'clients', c.id), { isOngoing: next, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error('toggle ongoing failed', err);
      setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, isOngoing: !next } : x)));
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (onlyOngoing && c.isOngoing !== true) return false;
      if (term && !(c.name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [clients, search, onlyOngoing]);

  const ongoing = filtered.filter((c) => c.isOngoing === true);
  // "Other clients": most recently worked-on first (by latest deliverable date); clients with no
  // recorded work fall to the end, ordered by name.
  const others = filtered.filter((c) => c.isOngoing !== true).slice().sort((a, b) => {
    const da = lastWorked[a.id] || '';
    const db_ = lastWorked[b.id] || '';
    if (da && db_) return db_.localeCompare(da);        // both worked → newer first
    if (da) return -1;                                  // a worked, b didn't
    if (db_) return 1;                                  // b worked, a didn't
    return (a.name || '').localeCompare(b.name || '');  // neither → by name
  });

  const Card = (c) => (
    <Link key={c.id} to={`/client-profitability/${c.id}`} className={styles.clientCard}>
      <button
        className={`${styles.starBtn} ${c.isOngoing ? styles.starOn : ''}`}
        title={c.isOngoing ? t.unmarkOngoing : t.markOngoing}
        aria-label={c.isOngoing ? t.unmarkOngoing : t.markOngoing}
        onClick={(e) => toggleOngoing(e, c)}
      >
        {c.isOngoing ? '★' : '☆'}
      </button>
      <div className={styles.clientName}>{c.name}</div>
      {c.phoneNumber && <div className={styles.clientMeta}>{c.phoneNumber}</div>}
      {c.isOngoing && <span className={styles.ongoingBadge}>⭐ {t.ongoingBadge}</span>}
      {canSeeMoney && (
        <div className={`${styles.owedChip} ${num(outstanding[c.id]) > 0 ? styles.owedDue : styles.owedClear}`}>
          {t.owes}: {(outstanding[c.id] || 0).toFixed(0)} {t.currency}
        </div>
      )}
      <div className={styles.clientCta}>{language === 'ar' ? 'عرض الربحية ←' : 'View profitability →'}</div>
    </Link>
  );

  return (
    <div className={styles.page} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>{t.listTitle}</h1>
          <p className={styles.subtitle}>{t.listSub}</p>
        </div>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      <div className={styles.listControls}>
        <input className={styles.search} placeholder={t.search} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className={styles.filterChips}>
          <button className={`${styles.chip} ${!onlyOngoing ? styles.chipActive : ''}`} onClick={() => setOnlyOngoing(false)}>{t.allClients}</button>
          <button className={`${styles.chip} ${onlyOngoing ? styles.chipActive : ''}`} onClick={() => setOnlyOngoing(true)}>⭐ {t.ongoingOnly}</button>
        </div>
      </div>

      {loading ? <p className={styles.muted}>…</p> : filtered.length === 0 ? (
        <p className={styles.muted}>{onlyOngoing ? t.noOngoing : t.noClients}</p>
      ) : (
        <>
          {ongoing.length > 0 && (
            <>
              <h3 className={styles.sectionHeading}>{t.ongoingSection}</h3>
              <div className={styles.clientGrid}>{ongoing.map(Card)}</div>
            </>
          )}
          {!onlyOngoing && others.length > 0 && (
            <>
              {ongoing.length > 0 && <h3 className={styles.sectionHeading}>{t.othersSection}</h3>}
              <div className={styles.clientGrid}>{others.map(Card)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------- Profile view ----------------
function ClientProfile({ clientId, t, isRTL, language, toggleLanguage, userRole, currentUser, canSeeMoney, canEnter }) {
  const [client, setClient] = useState(null);
  // Filtering: either a single month (existing) or a date-range period (like Work by Person).
  const [filterMode, setFilterMode] = useState('month'); // 'month' | 'period'
  const [monthKey, setMonthKey] = useState(monthOf(todayStr()));
  const [preset, setPreset] = useState('thisMonth');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Effective date range + month list the view covers.
  const view = useMemo(() => {
    if (filterMode === 'month') {
      const y = +monthKey.slice(0, 4), mo = +monthKey.slice(5, 7);
      const start = `${monthKey}-01`;
      const end = ymd(new Date(y, mo, 0)); // last day of month
      return { start, end, months: [monthKey] };
    }
    const { start, end } = rangeFor(preset, customStart, customEnd);
    return { start, end, months: monthsBetween(start, end) }; // months null = unbounded
  }, [filterMode, monthKey, preset, customStart, customEnd]);
  const { start: rStart, end: rEnd, months: rMonths } = view;

  const [clientDeliverables, setClientDeliverables] = useState([]); // this client, in range
  const [monthAll, setMonthAll] = useState([]); // all clients, in range (money roles only)
  const [charges, setCharges] = useState([]);
  const [collections, setCollections] = useState([]); // amounts actually collected from the client
  const [employees, setEmployees] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [compMap, setCompMap] = useState({}); // uid -> {dailyCost}
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);

  // Static lists (employees + freelancers) once.
  useEffect(() => {
    (async () => {
      try {
        const [uSnap, fSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), orderBy('name', 'asc'))),
          getDocs(collection(db, 'freelancers')),
        ]);
        const emps = uSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !isTerminated(u));
        setEmployees(emps);
        const cm = {};
        emps.forEach((u) => {
          const s = num(u.compensation?.monthlySalary);
          const wd = num(u.compensation?.workingDaysPerMonth);
          cm[u.id] = { dailyCost: s && wd ? s / wd : 0, name: u.name || u.email || '' };
        });
        setCompMap(cm);
        setFreelancers(fSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error('load lists failed', e); }
    })();
  }, []);

  // Client doc + data for the selected range.
  useEffect(() => {
    let cancelled = false;
    const monthsSet = rMonths ? new Set(rMonths) : null; // null = unbounded (all)
    (async () => {
      setLoading(true);
      try {
        const cSnap = await getDoc(doc(db, 'clients', clientId));
        if (!cancelled) setClient(cSnap.exists() ? { id: cSnap.id, ...cSnap.data() } : null);

        // This client's deliverables (single-field index on clientId), filtered to the range.
        const dSnap = await getDocs(query(collection(db, 'deliverables'), where('clientId', '==', clientId)));
        const mine = dSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((d) => (!rStart && !rEnd) ? true : inRange(d.date, rStart, rEnd));
        mine.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        if (!cancelled) setClientDeliverables(mine);

        // Charges (extra billed revenue) — filter by month (charges are month-tagged, no date).
        const chSnap = await getDocs(collection(db, 'clients', clientId, 'charges'));
        const ch = chSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => monthsSet ? monthsSet.has(c.month) : true);
        if (!cancelled) setCharges(ch);

        // Collections (amounts actually received) — filter by date within the range.
        const coSnap = await getDocs(collection(db, 'clients', clientId, 'collections'));
        const co = coSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => (!rStart && !rEnd) ? true : inRange(c.date, rStart, rEnd));
        if (!cancelled) setCollections(co);

        // All clients' deliverables in range — only money roles need it (for day-cost allocation).
        if (canSeeMoney) {
          let aq = collection(db, 'deliverables');
          if (rStart && rEnd) aq = query(aq, where('date', '>=', rStart), where('date', '<=', rEnd));
          else if (rStart) aq = query(aq, where('date', '>=', rStart));
          else if (rEnd) aq = query(aq, where('date', '<=', rEnd));
          const aSnap = await getDocs(aq);
          if (!cancelled) setMonthAll(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (e) { console.error('load client data failed', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, rStart, rEnd, rMonths, canSeeMoney, reload]);

  // Per-employee per-day ITEM counts across ALL clients this month (a deliverable of quantity N
  // counts as N items that day, so the daily cost is split per single item).
  const empDayCount = useMemo(() => {
    const map = {};
    monthAll.forEach((d) => {
      const qty = num(d.quantity) || 1;
      (d.executors || []).forEach((ex) => {
        if (ex.kind === 'employee' && ex.id) {
          const k = `${ex.id}|${d.date}`;
          map[k] = (map[k] || 0) + qty;
        }
      });
    });
    return map;
  }, [monthAll]);

  const freelancerRate = useCallback((freelancerId, typeKey) => {
    const f = freelancers.find((x) => x.id === freelancerId);
    return f ? num(f.rates?.[typeKey]) : 0;
  }, [freelancers]);

  // Cost of a deliverable (money roles). Employee/freelancer costs scale with the quantity;
  // manual extra costs are a flat amount on the whole deliverable.
  const deliverableCost = useCallback((d) => {
    const qty = num(d.quantity) || 1;
    let c = 0;
    (d.executors || []).forEach((ex) => {
      if (ex.kind === 'employee') {
        const daily = compMap[ex.id]?.dailyCost || 0;
        const cnt = empDayCount[`${ex.id}|${d.date}`] || qty;
        c += (daily / cnt) * qty; // (cost per single item) × quantity
      } else if (ex.kind === 'freelancer') {
        const per = (ex.costOverride !== undefined && ex.costOverride !== null && ex.costOverride !== '')
          ? num(ex.costOverride)
          : freelancerRate(ex.id, d.type);
        c += per * qty;
      }
    });
    (d.extraCosts || []).forEach((ec) => { c += num(ec.amount); });
    return c;
  }, [compMap, empDayCount, freelancerRate]);

  // Months the current view covers: the bounded range's months, or (unbounded) every month that
  // has data / a per-month retainer / a per-month quota.
  const billMonths = useMemo(() => {
    if (rMonths) return rMonths;
    const s = new Set(Object.keys(client?.monthlyRetainers || {}));
    Object.keys(client?.monthlyQuotasByMonth || {}).forEach((m) => s.add(m));
    clientDeliverables.forEach((d) => d.month && s.add(d.month));
    charges.forEach((c) => c.month && s.add(c.month));
    collections.forEach((c) => c.date && s.add(c.date.slice(0, 7)));
    return [...s];
  }, [rMonths, client, clientDeliverables, charges, collections]);

  const totals = useMemo(() => {
    const cost = canSeeMoney ? clientDeliverables.reduce((s, d) => s + deliverableCost(d), 0) : 0;
    const chargeSum = charges.reduce((s, c) => s + num(c.amount), 0);
    // Retainer can differ per month: sum each in-range month's value (default if not set per-month).
    const rMap = client?.monthlyRetainers || {};
    const retainerOf = (mk) => (Object.prototype.hasOwnProperty.call(rMap, mk) ? num(rMap[mk]) : num(client?.monthlyRetainer));
    const retainer = billMonths.reduce((s, mk) => s + retainerOf(mk), 0);
    const revenue = retainer + chargeSum; // billed / expected
    const collected = collections.reduce((s, c) => s + num(c.amount), 0); // actually received
    const outstanding = revenue - collected;
    return { cost, revenue, profit: revenue - cost, collected, outstanding, monthCount: billMonths.length || 1 };
  }, [clientDeliverables, charges, collections, client, billMonths, canSeeMoney, deliverableCost]);

  // Quota progress: count deliverables per type in range.
  const typeCounts = useMemo(() => {
    const m = {};
    clientDeliverables.forEach((d) => { m[d.type] = (m[d.type] || 0) + (num(d.quantity) || 1); });
    return m;
  }, [clientDeliverables]);

  // Quota targets are per-month (apply only to the month they were set). Sum each in-range month's
  // target for the type — NO fallback to a global quota (that's what "only that month" means).
  const quotaTargets = useMemo(() => {
    const byMonth = client?.monthlyQuotasByMonth || {};
    const agg = {};
    billMonths.forEach((mk) => {
      const q = byMonth[mk] || {};
      DELIVERABLE_TYPES.forEach((dt) => { agg[dt.key] = (agg[dt.key] || 0) + num(q[dt.key]); });
    });
    return agg;
  }, [client, billMonths]);

  return (
    <div className={styles.page} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.headerRow}>
        <div>
          <Link to="/client-profitability" className={styles.backLink}>{t.back}</Link>
          <h1 className={styles.title}>
            <button
              className={`${styles.starBtnInline} ${client?.isOngoing ? styles.starOn : ''}`}
              title={client?.isOngoing ? t.unmarkOngoing : t.markOngoing}
              aria-label={client?.isOngoing ? t.unmarkOngoing : t.markOngoing}
              onClick={async () => {
                if (!client) return;
                const next = !(client.isOngoing === true);
                setClient((c) => ({ ...c, isOngoing: next }));
                try { await updateDoc(doc(db, 'clients', clientId), { isOngoing: next, updatedAt: serverTimestamp() }); }
                catch (err) { console.error('toggle ongoing failed', err); setClient((c) => ({ ...c, isOngoing: !next })); }
              }}
            >
              {client?.isOngoing ? '★' : '☆'}
            </button>
            {client?.name || '—'}
          </h1>
        </div>
        <div className={styles.headerControls}>
          <div className={styles.modeToggle}>
            <button className={`${styles.modeBtn} ${filterMode === 'month' ? styles.modeActive : ''}`} onClick={() => setFilterMode('month')}>{t.modeMonth}</button>
            <button className={`${styles.modeBtn} ${filterMode === 'period' ? styles.modeActive : ''}`} onClick={() => setFilterMode('period')}>{t.modePeriod}</button>
          </div>
          {filterMode === 'month' ? (
            <input type="month" className={styles.monthInput} value={monthKey} onChange={(e) => setMonthKey(e.target.value)} />
          ) : (
            <>
              <select className={styles.monthInput} value={preset} onChange={(e) => setPreset(e.target.value)}>
                {PRESETS.map((p) => <option key={p.key} value={p.key}>{language === 'ar' ? p.ar : p.en}</option>)}
              </select>
              {preset === 'custom' && (
                <>
                  <input type="date" className={styles.monthInput} value={customStart} max={customEnd || undefined}
                    onChange={(e) => setCustomStart(e.target.value)} title={t.from} />
                  <input type="date" className={styles.monthInput} value={customEnd} min={customStart || undefined}
                    onChange={(e) => setCustomEnd(e.target.value)} title={t.to} />
                </>
              )}
            </>
          )}
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
      </div>

      {loading ? <p className={styles.muted}>…</p> : (
        <>
          {/* Financial summary — money roles only */}
          {canSeeMoney && (
            <div className={styles.statsRow}>
              <div className={`${styles.statCard} ${styles.statRevenue}`}>
                <div className={styles.statLabel}>{t.revenue}</div>
                <div className={styles.statValue}>{totals.revenue.toFixed(0)} {t.currency}</div>
              </div>
              <div className={`${styles.statCard} ${styles.statCost}`}>
                <div className={styles.statLabel}>{t.cost}</div>
                <div className={styles.statValue}>{totals.cost.toFixed(0)} {t.currency}</div>
              </div>
              <div className={`${styles.statCard} ${totals.profit >= 0 ? styles.statProfit : styles.statLoss}`}>
                <div className={styles.statLabel}>{t.profit}</div>
                <div className={styles.statValue}>{totals.profit.toFixed(0)} {t.currency}</div>
              </div>
              <div className={`${styles.statCard} ${styles.statCollected}`}>
                <div className={styles.statLabel}>{t.collected}</div>
                <div className={styles.statValue}>{totals.collected.toFixed(0)} {t.currency}</div>
              </div>
              <div className={`${styles.statCard} ${totals.outstanding > 0 ? styles.statOutstanding : styles.statClear}`}>
                <div className={styles.statLabel}>{t.outstanding}</div>
                <div className={styles.statValue}>{totals.outstanding.toFixed(0)} {t.currency}</div>
              </div>
            </div>
          )}

          {/* Quotas */}
          <QuotaSection t={t} language={language} targets={quotaTargets} typeCounts={typeCounts} />

          {/* Financial editor — money roles only */}
          {canSeeMoney && (
            <FinancialEditor
              t={t} language={language} client={client} clientId={clientId} monthKey={monthKey}
              charges={charges} collections={collections} currentUser={currentUser} onSaved={bump}
            />
          )}

          {/* Deliverables */}
          <DeliverablesSection
            t={t} language={language} canEnter={canEnter} canSeeMoney={canSeeMoney}
            clientId={clientId} clientName={client?.name || ''}
            deliverables={clientDeliverables} employees={employees} freelancers={freelancers}
            deliverableCost={deliverableCost} currentUser={currentUser} userRole={userRole} onChanged={bump}
          />
        </>
      )}
    </div>
  );
}

function QuotaSection({ t, language, targets, typeCounts }) {
  const rows = DELIVERABLE_TYPES.filter((dt) => num(targets[dt.key]) > 0 || typeCounts[dt.key]);
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{t.quotas}</h3>
      {rows.length === 0 ? <p className={styles.muted}>—</p> : (
        <div className={styles.quotaGrid}>
          {rows.map((dt) => {
            const target = num(targets[dt.key]);
            const done = typeCounts[dt.key] || 0;
            const pct = target ? Math.min(100, (done / target) * 100) : 100;
            const over = target && done > target;
            return (
              <div key={dt.key} className={styles.quotaItem}>
                <div className={styles.quotaHead}>
                  <span>{dt.icon} {deliverableLabel(dt.key, language)}</span>
                  <span className={over ? styles.quotaOver : ''}>
                    {done}{target ? ` ${t.quotaOf} ${target}` : ''}
                  </span>
                </div>
                <div className={styles.progressTrack}>
                  <div className={`${styles.progressFill} ${over ? styles.progressOver : ''}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FinancialEditor({ t, language, client, clientId, monthKey, charges, collections, currentUser, onSaved }) {
  const [open, setOpen] = useState(false);
  // The month this retainer applies to — editable here so you can set fees for any month directly.
  const [finMonth, setFinMonth] = useState(monthKey);
  useEffect(() => { setFinMonth(monthKey); }, [monthKey]); // follow the page's month when it changes
  // Retainer for the chosen month: the per-month value if set, else the general default.
  const monthRetainer = () => {
    const rMap = client?.monthlyRetainers || {};
    if (Object.prototype.hasOwnProperty.call(rMap, finMonth)) return rMap[finMonth];
    return client?.monthlyRetainer ?? '';
  };
  const [retainer, setRetainer] = useState(monthRetainer());
  // Quotas for the chosen month (fallback to the legacy global as a starting template only).
  const monthQuotas = () => {
    const q = {};
    const perMonth = client?.monthlyQuotasByMonth?.[finMonth];
    DELIVERABLE_TYPES.forEach((dt) => {
      q[dt.key] = (perMonth && perMonth[dt.key] != null) ? perMonth[dt.key] : (client?.monthlyQuotas?.[dt.key] ?? '');
    });
    return q;
  };
  const [quotaDraft, setQuotaDraft] = useState(monthQuotas());
  // Keep the inputs in sync when the chosen month (or client) changes.
  useEffect(() => { setRetainer(monthRetainer()); setQuotaDraft(monthQuotas()); }, [finMonth, client]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeDesc, setChargeDesc] = useState('');
  const [collAmount, setCollAmount] = useState('');
  const [collDesc, setCollDesc] = useState('');

  const saveFin = async () => {
    setSaving(true);
    const quotasClean = {};
    DELIVERABLE_TYPES.forEach((dt) => { const v = num(quotaDraft[dt.key]); if (v > 0) quotasClean[dt.key] = v; });
    try {
      await updateDoc(doc(db, 'clients', clientId), {
        // Store the retainer AND quotas for the chosen month specifically (months can differ).
        [`monthlyRetainers.${finMonth}`]: num(retainer),
        [`monthlyQuotasByMonth.${finMonth}`]: quotasClean,
        updatedAt: serverTimestamp(),
      });
      onSaved();
    } catch (e) { console.error('save financials failed', e); }
    finally { setSaving(false); }
  };

  const addCharge = async () => {
    if (num(chargeAmount) <= 0) return;
    try {
      await addDoc(collection(db, 'clients', clientId, 'charges'), {
        amount: num(chargeAmount), description: chargeDesc.trim(), month: finMonth,
        createdAt: serverTimestamp(), createdBy: currentUser?.uid || null,
      });
      setChargeAmount(''); setChargeDesc(''); onSaved();
    } catch (e) { console.error('add charge failed', e); }
  };

  const removeCharge = async (id) => {
    try { await deleteDoc(doc(db, 'clients', clientId, 'charges', id)); onSaved(); }
    catch (e) { console.error('remove charge failed', e); }
  };

  const addCollection = async () => {
    if (num(collAmount) <= 0) return;
    // Attribute the collection to the chosen month (today if it's the current month, else the 1st).
    const collDate = finMonth === monthOf(todayStr()) ? todayStr() : `${finMonth}-01`;
    try {
      await addDoc(collection(db, 'clients', clientId, 'collections'), {
        amount: num(collAmount), description: collDesc.trim(), month: finMonth,
        date: collDate,
        createdAt: serverTimestamp(), createdBy: currentUser?.uid || null,
      });
      setCollAmount(''); setCollDesc(''); onSaved();
    } catch (e) { console.error('add collection failed', e); }
  };

  const removeCollection = async (id) => {
    try { await deleteDoc(doc(db, 'clients', clientId, 'collections', id)); onSaved(); }
    catch (e) { console.error('remove collection failed', e); }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h3 className={styles.cardTitle}>{t.financials}</h3>
        <button className={styles.ghostBtn} onClick={() => setOpen((o) => !o)}>{t.editFin}</button>
      </div>

      {open && (
        <div className={styles.finEditor}>
          <div className={styles.finRow}>
            <label className={styles.field}>
              <span>{t.monthField}</span>
              <input type="month" value={finMonth} onChange={(e) => setFinMonth(e.target.value || monthKey)} />
            </label>
            <label className={styles.field}>
              <span>{t.retainer} ({t.currency})</span>
              <input type="number" min="0" value={retainer} onChange={(e) => setRetainer(e.target.value)} />
            </label>
          </div>
          <div className={styles.quotaTargetsTitle}>{t.quotaTargets} — {finMonth}</div>
          <div className={styles.quotaTargetsGrid}>
            {DELIVERABLE_TYPES.map((dt) => (
              <label key={dt.key} className={styles.rateField}>
                <span>{dt.icon} {deliverableLabel(dt.key, language)}</span>
                <input type="number" min="0" placeholder="0"
                  value={quotaDraft[dt.key]} onChange={(e) => setQuotaDraft((p) => ({ ...p, [dt.key]: e.target.value }))} />
              </label>
            ))}
          </div>
          <div className={styles.formActions}>
            <button className={styles.primaryBtn} disabled={saving} onClick={saveFin}>{t.saveFin}</button>
          </div>
        </div>
      )}

      {/* Extra charges */}
      <div className={styles.chargesBlock}>
        <div className={styles.chargesTitle}>{t.charges} — {finMonth}</div>
        {charges.length > 0 && (
          <ul className={styles.chargeList}>
            {charges.map((c) => (
              <li key={c.id} className={styles.chargeItem}>
                <span>{num(c.amount)} {t.currency}{c.description ? ` — ${c.description}` : ''}</span>
                <button className={styles.linkDel} onClick={() => removeCharge(c.id)}>{t.del}</button>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.chargeAdd}>
          <input type="number" min="0" placeholder={t.amount} value={chargeAmount} onChange={(e) => setChargeAmount(e.target.value)} />
          <input placeholder={t.description} value={chargeDesc} onChange={(e) => setChargeDesc(e.target.value)} />
          <button className={styles.smallBtn} onClick={addCharge}>{t.addCharge}</button>
        </div>
      </div>

      {/* Collections (amounts actually received) — separate from billed revenue */}
      <div className={styles.chargesBlock}>
        <div className={styles.chargesTitle}>{t.collections} — {finMonth}</div>
        {(collections || []).length > 0 && (
          <ul className={styles.chargeList}>
            {collections.map((c) => (
              <li key={c.id} className={styles.chargeItem}>
                <span>{num(c.amount)} {t.currency}{c.description ? ` — ${c.description}` : ''}{c.date ? ` (${c.date})` : ''}</span>
                <button className={styles.linkDel} onClick={() => removeCollection(c.id)}>{t.del}</button>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.chargeAdd}>
          <input type="number" min="0" placeholder={t.amount} value={collAmount} onChange={(e) => setCollAmount(e.target.value)} />
          <input placeholder={t.description} value={collDesc} onChange={(e) => setCollDesc(e.target.value)} />
          <button className={styles.smallBtn} onClick={addCollection}>{t.addCollection}</button>
        </div>
      </div>
    </div>
  );
}

function DeliverablesSection({
  t, language, canEnter, canSeeMoney, clientId, clientName,
  deliverables, employees, freelancers, deliverableCost, currentUser, userRole, onChanged,
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null); // deliverable id being edited (null = adding)
  const [type, setType] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(todayStr());
  const [execs, setExecs] = useState([]); // { kind, id, name, costOverride? (freelancer, string) }
  const [empPick, setEmpPick] = useState('');
  const [freePick, setFreePick] = useState('');
  const [extraCosts, setExtraCosts] = useState([]); // { amount, description }
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const activeFreelancers = useMemo(() => freelancers.filter((f) => f.active !== false), [freelancers]);
  const stdRate = (freelancerId, typeKey) => {
    const f = freelancers.find((x) => x.id === freelancerId);
    return f ? (num(f.rates?.[typeKey]) || 0) : 0;
  };

  const reset = () => {
    setEditingId(null); setType(''); setQuantity('1'); setTitle(''); setDate(todayStr());
    setExecs([]); setEmpPick(''); setFreePick(''); setExtraCosts([]); setErr('');
  };
  const closeForm = () => { reset(); setAdding(false); };

  const openAdd = () => { reset(); setAdding(true); };
  const openEdit = (d) => {
    setEditingId(d.id);
    setType(d.type || '');
    setQuantity(String(num(d.quantity) || 1));
    setTitle(d.title || '');
    setDate(d.date || todayStr());
    setExecs((d.executors || []).map((ex) => ex.kind === 'freelancer'
      ? { kind: 'freelancer', id: ex.id, name: ex.name || '', costOverride: (ex.costOverride !== undefined && ex.costOverride !== null) ? String(ex.costOverride) : '' }
      : { kind: 'employee', id: ex.id, name: ex.name || '' }));
    setExtraCosts((d.extraCosts || []).map((ec) => ({ amount: String(ec.amount ?? ''), description: ec.description || '' })));
    setEmpPick(''); setFreePick(''); setErr('');
    setAdding(true);
  };

  const addEmp = () => {
    if (!empPick) return;
    const u = employees.find((e) => e.id === empPick);
    if (u && !execs.some((x) => x.kind === 'employee' && x.id === u.id)) {
      setExecs((p) => [...p, { kind: 'employee', id: u.id, name: u.name || u.email || '' }]);
    }
    setEmpPick('');
  };
  const addFree = () => {
    if (!freePick) return;
    const f = activeFreelancers.find((x) => x.id === freePick);
    if (f && !execs.some((x) => x.kind === 'freelancer' && x.id === f.id)) {
      setExecs((p) => [...p, { kind: 'freelancer', id: f.id, name: f.name || '', costOverride: '' }]);
    }
    setFreePick('');
  };
  const removeExec = (i) => setExecs((p) => p.filter((_, idx) => idx !== i));
  const setExecOverride = (i, val) => setExecs((p) => p.map((x, idx) => idx === i ? { ...x, costOverride: val } : x));

  const save = async () => {
    if (!type) { setErr(t.typeRequired); return; }
    if (execs.length === 0) { setErr(t.execRequired); return; }
    setSaving(true);

    const orig = editingId ? deliverables.find((d) => d.id === editingId) : null;
    // Build executors. Freelancer cost override comes from the form for money roles; for non-money
    // editors it is preserved from the original deliverable (they can't see/edit prices).
    const executors = execs.map((ex) => {
      if (ex.kind !== 'freelancer') return { kind: 'employee', id: ex.id, name: ex.name };
      const base = { kind: 'freelancer', id: ex.id, name: ex.name };
      let override;
      if (canSeeMoney) {
        override = (ex.costOverride === '' || ex.costOverride == null) ? undefined : num(ex.costOverride);
      } else {
        const o = orig?.executors?.find((x) => x.kind === 'freelancer' && x.id === ex.id);
        override = (o && o.costOverride != null && o.costOverride !== '') ? num(o.costOverride) : undefined;
      }
      return override === undefined ? base : { ...base, costOverride: override };
    });

    const cleanExtra = extraCosts
      .map((ec) => ({ amount: num(ec.amount), description: (ec.description || '').trim() }))
      .filter((ec) => ec.amount > 0);

    const common = {
      type, quantity: Math.max(1, Math.round(num(quantity) || 1)),
      title: title.trim(), date, month: monthOf(date), executors,
      updatedAt: serverTimestamp(),
    };
    // Only money roles may change extra costs; otherwise leave the existing ones untouched.
    if (canSeeMoney) common.extraCosts = cleanExtra;

    try {
      if (editingId) {
        await updateDoc(doc(db, 'deliverables', editingId), {
          ...common,
          updatedBy: currentUser?.uid || null,
          updatedByName: currentUser?.displayName || currentUser?.email || '',
        });
      } else {
        await addDoc(collection(db, 'deliverables'), {
          clientId, clientName, ...common,
          extraCosts: canSeeMoney ? cleanExtra : [],
          createdBy: currentUser?.uid || null,
          createdByName: currentUser?.displayName || currentUser?.email || '',
          createdByRole: userRole || '',
          createdAt: serverTimestamp(),
        });
      }
      closeForm(); onChanged();
    } catch (e) { console.error('save deliverable failed', e); setErr('حدث خطأ أثناء الحفظ.'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelDeliverable)) return;
    try { await deleteDoc(doc(db, 'deliverables', id)); onChanged(); }
    catch (e) { console.error('delete deliverable failed', e); }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h3 className={styles.cardTitle}>{t.deliverables}</h3>
        {canEnter && !adding && <button className={styles.primaryBtn} onClick={openAdd}>+ {t.addDeliverable}</button>}
      </div>

      {adding && (
        <div className={styles.deliverableForm}>
          <div className={styles.priceEditorTitle}>{editingId ? t.editDeliverable : t.addDeliverable}</div>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>{t.type}</span>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">{t.pickType}</option>
                {DELIVERABLE_TYPES.map((dt) => <option key={dt.key} value={dt.key}>{dt.icon} {deliverableLabel(dt.key, language)}</option>)}
              </select>
            </label>
            <label className={styles.field}>
              <span>{t.quantity}</span>
              <input type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>{t.date}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>{t.title}</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
          </div>

          <div className={styles.execBlock}>
            <div className={styles.execLabel}>{t.executors}</div>
            <div className={styles.execChips}>
              {execs.map((ex, i) => (
                <span key={i} className={`${styles.execChip} ${ex.kind === 'freelancer' ? styles.chipFree : styles.chipEmp}`}>
                  {ex.kind === 'freelancer' ? '🧑‍💻' : '👤'} {ex.name}
                  {ex.kind === 'freelancer' && canSeeMoney && (
                    <input
                      type="number" min="0" className={styles.chipPrice}
                      title={`${t.price} · ${t.revertHint}`}
                      placeholder={`${t.standardRate}: ${stdRate(ex.id, type)}`}
                      value={ex.costOverride ?? ''}
                      onChange={(e) => setExecOverride(i, e.target.value)}
                    />
                  )}
                  <button onClick={() => removeExec(i)} aria-label="remove">×</button>
                </span>
              ))}
              {execs.length === 0 && <span className={styles.muted}>—</span>}
            </div>
            <div className={styles.execPickers}>
              <div className={styles.pickerRow}>
                <select value={empPick} onChange={(e) => setEmpPick(e.target.value)}>
                  <option value="">{t.employee}…</option>
                  {employees.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
                <button className={styles.smallBtn} onClick={addEmp}>{t.addEmp}</button>
              </div>
              <div className={styles.pickerRow}>
                <select value={freePick} onChange={(e) => setFreePick(e.target.value)}>
                  <option value="">{t.freelancer}…</option>
                  {activeFreelancers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <button className={styles.smallBtn} onClick={addFree}>{t.addFree}</button>
              </div>
            </div>
            {canSeeMoney && execs.some((x) => x.kind === 'freelancer') && (
              <p className={styles.priceHint}>{t.unitPrice} · {t.revertHint}</p>
            )}
          </div>

          {/* Extra costs — money roles only */}
          {canSeeMoney && (
            <div className={styles.extraCostBlock}>
              <div className={styles.execLabel}>{t.extraCost}</div>
              {extraCosts.map((ec, i) => (
                <div key={i} className={styles.pickerRow}>
                  <input type="number" min="0" placeholder={t.amount} value={ec.amount}
                    onChange={(e) => setExtraCosts((p) => p.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} />
                  <input placeholder={t.description} value={ec.description}
                    onChange={(e) => setExtraCosts((p) => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                  <button className={styles.linkDel} onClick={() => setExtraCosts((p) => p.filter((_, idx) => idx !== i))}>{t.del}</button>
                </div>
              ))}
              <button className={styles.smallBtn} onClick={() => setExtraCosts((p) => [...p, { amount: '', description: '' }])}>{t.addExtraCost}</button>
            </div>
          )}

          {err && <p className={styles.err}>{err}</p>}
          <div className={styles.formActions}>
            <button className={styles.ghostBtn} onClick={closeForm}>{t.cancel}</button>
            <button className={styles.primaryBtn} disabled={saving} onClick={save}>{t.save}</button>
          </div>
        </div>
      )}

      {deliverables.length === 0 ? <p className={styles.muted}>{t.noDeliverables}</p> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t.type}</th>
                <th>{t.quantity}</th>
                <th>{t.title}</th>
                <th>{t.date}</th>
                <th>{t.executors}</th>
                {canSeeMoney && <th>{t.cost}</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deliverables.map((d) => (
                <tr key={d.id}>
                  <td data-label={t.type}>{deliverableIcon(d.type)} {deliverableLabel(d.type, language)}</td>
                  <td data-label={t.quantity}><strong>×{num(d.quantity) || 1}</strong></td>
                  <td data-label={t.title}>{d.title || '—'}</td>
                  <td data-label={t.date}>{d.date}</td>
                  <td data-label={t.executors}>
                    <div className={styles.execCell}>
                      {(d.executors || []).map((ex, i) => (
                        <span key={i} className={`${styles.execChipSmall} ${ex.kind === 'freelancer' ? styles.chipFree : styles.chipEmp}`}>
                          {ex.kind === 'freelancer' ? '🧑‍💻' : '👤'} {ex.name}
                          {ex.kind === 'freelancer' && ex.costOverride !== undefined && ex.costOverride !== null && ex.costOverride !== '' && (
                            <em className={styles.overrideTag}>✎{num(ex.costOverride)}</em>
                          )}
                        </span>
                      ))}
                    </div>
                  </td>
                  {canSeeMoney && <td data-label={t.cost}><strong>{deliverableCost(d).toFixed(0)} {t.currency}</strong></td>}
                  <td>
                    <div className={styles.rowActions}>
                      {canEnter && <button className={styles.smallBtn} onClick={() => openEdit(d)}>✏️ {t.edit}</button>}
                      {canEnter && <button className={`${styles.smallBtn} ${styles.delBtn}`} onClick={() => remove(d.id)}>{t.del}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
